import { getConfig } from '@/lib/config'
import axios from 'axios'

const { publicRuntimeConfig } = getConfig()

// Get the appropriate API host based on environment
function getApiHost(): string {
  return publicRuntimeConfig.apiHost
}

// Auth response type
interface AuthResponse {
  success: boolean
  message?: string
  data: any
}

// Token cookie name (exactly matching NuxtApp pattern)
const AUTH_TOKEN_COOKIE = 'auth._token.local'

// Simple cookie helper functions (exactly matching NuxtApp behavior)
export function storeAuthToken(token: string): void {
  // Set token exactly like NuxtApp auth module does
  const expirationDate = new Date()
  expirationDate.setDate(expirationDate.getDate() + 90) // 90 days like NuxtApp

  // Determine domain exactly like NuxtApp
  let domain = window.location.hostname
  if (domain.includes('gsudevelopment.com')) {
    domain = '.gsudevelopment.com'
  } else if (domain.includes('getsetup.com') || domain.includes('getsetup.io')) {
    const parts = domain.split('.')
    if (parts.length >= 2) {
      domain = `.${parts.slice(-2).join('.')}`
    }
  }

  const secure = window.location.protocol === 'https:'
  const cookieString = `${AUTH_TOKEN_COOKIE}=${token}; path=/; domain=${domain}; ${
    secure ? 'secure; ' : ''
  }expires=${expirationDate.toUTCString()}`

  console.log('Setting auth token cookie:', { token: token.substring(0, 10) + '...', domain, secure })
  document.cookie = cookieString

  // Verify cookie was set
  setTimeout(() => {
    const checkToken = getAuthToken()
    console.log('Cookie verification:', checkToken ? 'SUCCESS' : 'FAILED')
  }, 100)
}

export function getAuthToken(): string | null {
  if (typeof document === 'undefined') return null

  const cookies = document.cookie.split(';')
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=')
    if (name === AUTH_TOKEN_COOKIE) {
      return value || null
    }
  }
  return null
}

export function clearAuthToken(): void {
  let domain = window.location.hostname
  if (domain.includes('gsudevelopment.com')) {
    domain = '.gsudevelopment.com'
  } else if (domain.includes('getsetup.com') || domain.includes('getsetup.io')) {
    const parts = domain.split('.')
    if (parts.length >= 2) {
      domain = `.${parts.slice(-2).join('.')}`
    }
  }

  document.cookie = `${AUTH_TOKEN_COOKIE}=; path=/; domain=${domain}; expires=Thu, 01 Jan 1970 00:00:00 GMT`
}

// Replicate EXACT NuxtApp login pattern
export async function login(
  email: string,
  password: string,
  country: string = 'US',
): Promise<{
  success: boolean
  data?: any
  message?: string
  error?: any
}> {
  try {
    // Make EXACT same API call as NuxtApp: this.$auth.loginWith('local', { data: { country, email, password } })
    const response = await axios.post(`${getApiHost()}/auth/login`, {
      country,
      email,
      password,
    })

    console.log('Login API response:', response.data)

    // Handle response exactly like NuxtApp does
    const {
      data: { error, message, user, token, partners },
    } = response

    if (message === 'USER_AUTHENTICATION_SUCCESS') {
      return {
        success: true,
        data: { user, token, partners, message },
        message,
      }
    } else {
      return {
        success: false,
        message,
        error,
      }
    }
  } catch (error: any) {
    console.error('Login error:', error)
    return {
      success: false,
      message: error.response?.data?.message || 'Login failed',
      error,
    }
  }
}

// Handle auth success exactly like NuxtApp: this.$auth.setUserToken(token) + this.$auth.setUser(user)
export function handleAuthSuccess(data: any, setUser: (user: any) => void): void {
  console.log('Handling auth success with data:', data)

  if (data?.token) {
    // Store token exactly like NuxtApp auth module
    storeAuthToken(data.token)
  }

  if (data?.user) {
    // Set user exactly like NuxtApp
    setUser(data.user)
  }

  // Handle partner cookie exactly like NuxtApp
  if (data?.partners?.length) {
    const partner = data.partners[0] // Use first partner like NuxtApp
    const partnerCookie = JSON.stringify(partner)

    let domain = window.location.hostname
    if (domain.includes('gsudevelopment.com')) {
      domain = '.gsudevelopment.com'
    } else if (domain.includes('getsetup.com') || domain.includes('getsetup.io')) {
      const parts = domain.split('.')
      if (parts.length >= 2) {
        domain = `.${parts.slice(-2).join('.')}`
      }
    }

    const secure = window.location.protocol === 'https:'
    document.cookie = `auth.partner=${partnerCookie}; path=/; domain=${domain}; ${
      secure ? 'secure; ' : ''
    }expires=${new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toUTCString()}`
  }
}

// Register function (matching NuxtApp pattern exactly)
export async function register(
  firstName: string,
  lastName: string,
  email: string,
  password: string,
  country: string = 'US',
): Promise<{
  success: boolean
  data?: any
  message?: string
  error?: any
}> {
  try {
    console.log('Making registration API call with:', { firstName, lastName, email, country })

    const response = await axios.post(`${getApiHost()}/auth/register/email`, {
      first_name: firstName,
      last_name: lastName,
      email,
      password,
      country,
    })

    console.log('Registration API response:', response.data)

    // Handle response exactly like NuxtApp does - check for both success messages
    const { message, user, token, entitlements } = response.data

    if (message === 'USER_REGISTRATION_SUCCESS' || message === 'USER_AUTHENTICATION_SUCCESS') {
      return {
        success: true,
        data: { user, token, entitlements, message },
        message,
      }
    } else {
      return {
        success: false,
        message,
        error: response.data.error,
      }
    }
  } catch (error: any) {
    console.error('Registration error:', error)
    return {
      success: false,
      message: error.response?.data?.message || 'Registration failed',
      error,
    }
  }
}

// Facebook authentication (keeping existing pattern)
export async function facebookAuth(
  accessToken: string,
  partnerData?: {
    lead_id?: string | null
    source?: string
    partnerMetadata?: string
  },
): Promise<AuthResponse> {
  try {
    const apiHost = getApiHost()

    const payload: any = {
      access_token: accessToken,
      country: 'US',
    }

    if (partnerData) {
      if (partnerData.lead_id) payload.lead_id = partnerData.lead_id
      if (partnerData.source) payload.source = partnerData.source
      if (partnerData.partnerMetadata) payload.partnerMetadata = partnerData.partnerMetadata
    }

    const response = await axios.post(`${apiHost}/auth/facebook/login`, payload)

    return {
      success: true,
      message: response.data.message,
      data: response.data,
    }
  } catch (error: any) {
    console.error('Facebook auth error:', error)
    return {
      success: false,
      message: error.response?.data?.message || 'Facebook authentication failed',
      data: null,
    }
  }
}

// Password reset function (keeping existing pattern)
export async function resetPassword(email: string): Promise<{
  success: boolean
  message?: string
  error?: any
}> {
  try {
    const response = await axios.post(`${getApiHost()}/auth/forgot-password`, {
      email,
    })

    return {
      success: true,
      message: response.data.message,
    }
  } catch (error: any) {
    return {
      success: false,
      message: error.response?.data?.message || 'Password reset failed',
      error,
    }
  }
}

// Create authenticated fetch function for API calls
export function createAuthenticatedFetch(): (url: string, options?: RequestInit) => Promise<Response> {
  return async (url: string, options: RequestInit = {}) => {
    const token = getAuthToken()
    const headers = {
      ...options.headers,
      ...(token && { Authorization: token }), // No "Bearer" prefix (matches Nuxt tokenType: false)
    }

    return fetch(url, {
      ...options,
      headers,
      credentials: 'include',
    })
  }
}

// Handle authentication errors (exactly matching NuxtApp messages)
export function getAuthErrorMessage(message: string): string {
  switch (message) {
    case 'USER_AUTHENTICATION_SUCCESS':
      return 'Login successful'
    case 'INVALID_PASSWORD':
      return 'Incorrect password'
    case 'USER_NOT_FOUND':
      return 'Email not registered'
    case 'USER_ALREADY_REGISTERED':
      return 'This email address is already registered'
    case 'VALIDATION_ERROR':
      return 'Please enter valid information'
    case 'RATE_LIMIT':
      return 'Too many attempts. Please try again later.'
    default:
      return 'Something went wrong. Please try again.'
  }
}
